import type { SetStateAction, Dispatch } from "react";
import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { ModelModal } from "@/components/Modals/ModelModal";
import {
  ModelStatusBadge,
  type ModelStatus,
} from "@/components/Views/Models/ModelStatusBadge";
import { NoxusSortable } from "../UI/NoxusSortable";
import { DATA_KEYS } from "@/utils/useRemoteData";

import { useQuery } from "@tanstack/react-query";
import { modelsErrorReason, useModels } from "@/hooks/useModels";
import { useTranslations } from "next-intl";
import {
  ModelDefinition,
  InternalService,
  PresetsService,
  ProvidersService,
} from "@/generated";
import type { PresetResponse } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import {
  ChevronDown,
  GripVertical,
  Link2Off,
  SquareLibrary,
  Settings2,
  MousePointer2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../shadcn/dropdown-menu";
import ImageWithFallback from "../ImageWithFallback";
import { normalizeValue } from "@/utils/generic";
import ErrorBoundary from "../ErrorBoundary";
import { NoxusAlert } from "../UI/NoxusAlert";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import { RetirementNotice } from "../Views/Models/RetirementNotice";
import { ExternalLink } from "lucide-react";
import { Button } from "../shadcn/button";
import { Skeleton } from "../shadcn/skeleton";
import { cn } from "@/lib/utils";
import { resolvePresetIcon } from "@/utils/presetIcons";
import { PinFieldContext } from "./NoxusFormLabel";
import { PinHandle } from "@/components/SpotflowV2/Sidebar/PinHandle";
import { ConfigFormRendererContext } from "./ConfigFormRenderer";

interface ModelHealthFields {
  lifecycle_state?: string;
  last_error?: string | null;
  consecutive_failures?: number;
  last_checked_at?: string | null;
}

export interface IModelExtended extends ModelDefinition {
  id: string;
  speed: number;
  quality: number;
  normalizedSpeed: number;
}

// Provider logos aren't on ModelDefinition. The Chosen-models list reads
// from this context (populated once in InnerModelSelectField) so each
// SortableModelItem can render the provider chip with the same logo +
// name shown on the Providers tab.
const ProviderLogoContext = createContext<Record<string, string>>({});
// Model id to display name, so a retirement hint can name the replacement
// the way the picker names models.
const ModelTitleContext = createContext<Record<string, string>>({});

export type ModelPickerLayout = "stacked" | "side_by_side";

// User-facing noun per picker flavour. Keyed lookup rather than a chain of
// `modelType === "embeddings"` ternaries, so a new flavour reads correctly
// everywhere instead of falling back to the generic "models" copy.
const MODEL_TYPE_NOUN: Record<string, string> = {
  embeddings: "embedding models",
  rerankers: "rerankers",
};

const modelNoun = (modelType: string): string =>
  MODEL_TYPE_NOUN[modelType] ?? "models";

const modelNounTitle = (modelType: string): string => {
  const noun = modelNoun(modelType);
  return noun.charAt(0).toUpperCase() + noun.slice(1);
};

// Rerankers and embeddings are managed on their own settings tabs.
const MODEL_TYPE_SETTINGS_TAB: Record<string, string> = {
  embeddings: "/settings/platform/models?tab=embeddings",
  rerankers: "/settings/platform/models?tab=rerankers",
};

const modelSettingsHref = (modelType: string): string =>
  MODEL_TYPE_SETTINGS_TAB[modelType] ?? "/settings/platform/models";

interface IModelSelectField {
  name: string;
  endpoint: string;
  modelType: string;
  defaultValue?: string | string[];
  required: boolean;
  label?: string;
  maxModels?: number;
  isDisabled?: boolean;
  showPresets?: boolean;
  showInlinePresets?: boolean;
  layout?: ModelPickerLayout;
  hideDisabledModels?: boolean;
  defaultPreset?: string;
}

interface IInnerModelSelectField {
  name: string;
  endpoint: string;
  modelType: string;
  defaultValue?: string | string[];
  required: boolean;
  visibleModelsUpdatedData: IModelExtended[];
  allModelsUpdatedData: IModelExtended[];
  label?: string;
  maxModels?: number;
  isDisabled?: boolean;
  showPresets?: boolean;
  showInlinePresets?: boolean;
  layout?: ModelPickerLayout;
  hideDisabledModels?: boolean;
  defaultPreset?: string;
}

const SortableModelItem = ({
  id,
  index,
  item,
}: {
  id: string;
  index: number;
  item: IModelExtended;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  // ``item.provider_image`` is populated server-side from the no-llm
  // provider TYPE's logo (see ``ModelDefinition.from_no_llm_model``).
  // The context map is a type-keyed fallback for legacy rows that
  // skipped the per-link emission path.
  const providerLogos = useContext(ProviderLogoContext);
  const modelTitles = useContext(ModelTitleContext);
  const itemType = (item as { provider_type?: string }).provider_type;
  const providerLogo =
    item.provider_image ?? (itemType ? providerLogos[itemType] : undefined);
  const isProviderDead = item.provider_is_active === false;
  const isLinkDisabled = item.is_active === false;
  const isUnavailable = isProviderDead || isLinkDisabled;

  const unavailableTooltip = isLinkDisabled
    ? "Model disabled: enable it in Settings → Models"
    : "Provider disconnected: enable it in Settings → Models";

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "w-full flex items-center justify-between",
        isUnavailable && "opacity-45"
      )}
      style={style}
    >
      <p className="text-primary text-xs mr-4 min-w-5">{`${index + 1}.`}</p>
      <div className="flex items-center justify-start flex-1 gap-2 min-w-0">
        <div className="relative h-4 w-4 shrink-0">
          <ImageWithFallback
            fill
            src={item.image || "/images/placeholder.svg"}
            alt={`Logo for ${item.title}`}
            className="dark:invert dark:brightness-0"
          />
        </div>
        <p className="text-xs truncate">{item.title}</p>
        <RetirementNotice
          retirementDate={item.retirement_date}
          replacedBy={
            item.replaced_by
              ? (modelTitles[item.replaced_by] ?? item.replaced_by)
              : null
          }
        />
        {item.provider && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-muted text-[11px] text-muted-foreground shrink-0">
            {item.provider}
          </span>
        )}
        {(() => {
          if (isUnavailable)
            return (
              <NoxusTooltip
                content={unavailableTooltip}
                placement="top"
                classNameTrigger="flex items-center leading-none h-3"
              >
                <Link2Off
                  className="size-3 shrink-0 text-muted-foreground"
                  aria-label={unavailableTooltip}
                />
              </NoxusTooltip>
            );
          const health = item as unknown as ModelHealthFields;
          const ls = health.lifecycle_state;
          const statusMap: Record<string, ModelStatus> = {
            degraded: "degraded",
            suspended: "suspended",
            auto_deactivated: "auto_deactivated",
            manually_deactivated: "deactivated",
          };
          if (ls && statusMap[ls])
            return (
              <ModelStatusBadge
                status={statusMap[ls]}
                lastError={health.last_error}
                consecutiveFailures={health.consecutive_failures}
                lastCheckedAt={health.last_checked_at}
                iconOnly
              />
            );
          return null;
        })()}
      </div>
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="text-muted-foreground"
      >
        <GripVertical size="1rem" strokeWidth="1.25px" />
      </button>
    </div>
  );
};

// TODO update in the future to use our new services
export const ModelSelectField = ({
  name,
  endpoint,
  modelType,
  defaultValue,
  required,
  label,
  maxModels,
  isDisabled,
  showPresets = true,
  showInlinePresets = true,
  layout = "stacked",
  hideDisabledModels = true,
  defaultPreset,
}: IModelSelectField) => {
  const t = useTranslations("modelPicker");
  const {
    data: modelsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useModels(endpoint);

  /* Changes the models structure to what the modal expects*/
  const modelsUpdatedData: IModelExtended[] = useMemo(() => {
    // ``modelsData`` should be an array, but a 4xx/5xx from
    // ``/models/llms`` (e.g. tenant has no Noxus tenant pinned, an
    // unhandled exception in the backend) lands here as the raw error
    // payload — an object, not an array. Guard with Array.isArray so
    // the ErrorBoundary doesn't blow up the whole Model tab.
    if (!Array.isArray(modelsData)) {
      return [];
    }

    // The catalog (left panel in "Edit models") only shows models the
    // user can actually dispatch — active link + active provider + valid
    // config. Disabled/disconnected models still appear in the
    // chosen-models list (via allModelsUpdatedData) with a broken-link
    // icon, but are not offered for new selection here.
    const visibleModels = modelsData.filter(
      (model) =>
        model.visible !== false &&
        model.is_active !== false &&
        model.provider_is_active !== false
    );

    const qualityArray = visibleModels.map((el) => el.properties.quality);
    const speedArray = visibleModels.map((el) => el.properties.speed);
    return (
      visibleModels?.map((model) => ({
        // Per-link emission: same model can appear twice (one row per
        // linked provider). Keying on slug keeps each row distinct;
        // ``name`` would collide.
        id: model.slug ?? model.name,
        ...model,
        speed: model.properties.speed,
        quality: model.properties.quality,
        normalizedSpeed: normalizeValue(
          model.properties.speed,
          speedArray,
          modelType === "text" ? 214 : 2
        ),
      })) || []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelsData]);

  /* Changes the models structure to what the modal expects*/
  const allModelsUpdatedData: IModelExtended[] = useMemo(() => {
    // Same defensive guard as ``modelsUpdatedData`` above — error
    // payloads come back as objects and would crash .map().
    if (!Array.isArray(modelsData)) {
      return [];
    }

    const qualityArray = modelsData.map((el) => el.properties.quality);
    const speedArray = modelsData.map((el) => el.properties.speed);
    const result = modelsData.map((model) => ({
      id: model.slug ?? model.name,
      ...model,
      speed: model.properties.speed,
      quality: normalizeValue(model.properties.quality, qualityArray, 80),
      normalizedSpeed: normalizeValue(
        model.properties.speed,
        speedArray,
        modelType === "text" ? 214 : 2
      ),
    }));

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelsData]);

  // Gate on ``modelsData === undefined`` (not just ``isLoading``).
  // react-query reports ``isLoading=false`` for one render between
  // mount and the first fetch resolving — without this guard the
  // empty-state warning flashes for a tick before the models arrive.
  const stillFetching = isLoading || modelsData === undefined;
  // If the response came back but isn't an array, treat as an error
  // (most likely a 4xx/5xx error payload), so the loading skeleton
  // gets replaced with the picker's error state instead of the
  // ErrorBoundary blanking the whole panel.
  const responseShapeBroken = !stillFetching && !Array.isArray(modelsData);

  return isError || responseShapeBroken || stillFetching ? (
    <div className="w-full border border-border rounded-lg p-4">
      <div className="w-full flex items-center justify-between mb-2">
        <p className="text-sm">Chosen models</p>
      </div>
      <p className="text-muted-foreground text-sm mb-4">
        Your first selected model will be set as the default. If it encounters
        an error, the next one in line will be used automatically.
      </p>
      {isError && (
        <NoxusAlert
          size="xs"
          title={t("loadFailed")}
          description={modelsErrorReason(error) ?? t("loadFailedHint")}
          variant="destructive"
          action={{ label: t("tryAgain"), onClick: () => void refetch() }}
        />
      )}

      {stillFetching && (
        <div className="w-full flex flex-col gap-2">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
        </div>
      )}
    </div>
  ) : allModelsUpdatedData.length === 0 ? (
    <NoxusAlert
      variant="warning"
      size="xs"
      title={`No ${modelNoun(modelType)} configured`}
      description={`You have no ${modelNoun(modelType)} configured. Add them in the Models page in Settings.`}
      action={{
        label: (
          <>
            <ExternalLink size={12} />
            Go to Models
          </>
        ),
        onClick: () =>
          window.open(modelSettingsHref(modelType), "_blank", "noopener"),
      }}
    />
  ) : (
    <InnerModelSelectField
      name={name}
      endpoint={endpoint}
      modelType={modelType}
      defaultValue={defaultValue}
      required={required}
      visibleModelsUpdatedData={modelsUpdatedData}
      allModelsUpdatedData={allModelsUpdatedData}
      label={label}
      maxModels={maxModels}
      isDisabled={isDisabled}
      showPresets={showPresets}
      showInlinePresets={showInlinePresets}
      layout={layout}
      hideDisabledModels={hideDisabledModels}
      defaultPreset={defaultPreset}
    />
  );
};

// ──────────────────────────────────────────────────
// Preset Selector (inline in model select field)
// ──────────────────────────────────────────────────

export function PresetSelector({
  allModelsUpdatedData,
  onSelectPreset,
  onPresetApply,
  isDisabled,
  onCustomClick,
  resetKey = 0,
  defaultPreset,
  activePreset: controlledActivePreset,
  onActivePresetChange,
}: {
  allModelsUpdatedData: IModelExtended[];
  onSelectPreset: (models: IModelExtended[]) => void;
  /** Called when a preset is selected. Passes the handle and resolved models. */
  onPresetApply?: (handle: string, models: IModelExtended[]) => void;
  isDisabled?: boolean;
  onCustomClick?: () => void;
  /** Increment to reset the active preset label back to "Presets". */
  resetKey?: number;
  /** Preset handle to show as active on first render. */
  defaultPreset?: string;
  /** Controlled active preset (overrides internal state). */
  activePreset?: string | null;
  /** Called when the active preset changes. */
  onActivePresetChange?: (preset: string | null) => void;
}) {
  const { spotGroup } = useSpotUser();
  const { data: presetsResponse } = useQuery({
    queryKey: [DATA_KEYS.MODEL_PRESETS, spotGroup.id],
    queryFn: () =>
      PresetsService.listPresets({
        path: { group_id: spotGroup.id },
      }),
  });
  const [internalActivePreset, setInternalActivePreset] = useState<
    string | null
  >(defaultPreset ?? null);
  const isControlled = controlledActivePreset !== undefined;
  const activePreset = isControlled
    ? controlledActivePreset
    : internalActivePreset;
  const setActivePreset = useCallback(
    (preset: string | null) => {
      if (!isControlled) setInternalActivePreset(preset);
      onActivePresetChange?.(preset);
    },
    [isControlled, onActivePresetChange]
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const presetsArray = useMemo(() => {
    const presets = presetsResponse?.data;
    if (!presets) return [];
    return (presets as PresetResponse[]).filter((p) => p.is_active !== false);
  }, [presetsResponse]);

  const handlePresetClick = useCallback(
    (preset: PresetResponse) => {
      if (isDisabled) return;
      const resolved = preset.models.flatMap((name) =>
        expandStoredValueToRows(allModelsUpdatedData, name)
      );
      if (onPresetApply) {
        onPresetApply(preset.handle, resolved);
      } else {
        onSelectPreset(resolved);
      }
      setActivePreset(preset.handle);
      setDropdownOpen(false);
    },
    [
      allModelsUpdatedData,
      onSelectPreset,
      onPresetApply,
      isDisabled,
      setActivePreset,
    ]
  );

  const handleCustomClick = useCallback(() => {
    setActivePreset("Custom");
    setDropdownOpen(false);
    onCustomClick?.();
  }, [onCustomClick, setActivePreset]);

  const activePresetData = useMemo(
    () => presetsArray.find((p) => p.handle === activePreset),
    [presetsArray, activePreset]
  );

  const TriggerIcon = useMemo(
    () =>
      activePreset === "Custom"
        ? Settings2
        : ((activePresetData
            ? resolvePresetIcon(activePresetData.icon)
            : null) ?? SquareLibrary),
    [activePreset, activePresetData]
  );

  if (presetsArray.length === 0 && !onCustomClick) return null;

  return (
    <DropdownMenu
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      modal={false}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isDisabled}
          className="gap-1.5 max-w-48"
          onClick={(e) => e.stopPropagation()}
        >
          <TriggerIcon size="0.75rem" className="shrink-0" />
          <span className="truncate leading-4">
            {activePresetData?.title || activePreset || "Presets"}
          </span>
          <ChevronDown size="0.75rem" className="shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onPointerDownOutside={(e) => e.stopPropagation()}
      >
        <div className="max-h-64 overflow-y-auto">
          <h5 className="text-xs font-medium mb-2 ml-2 mt-2 text-muted-foreground">
            Model presets
          </h5>
          <div className="flex flex-col gap-0.5">
            {presetsArray.map((preset) => {
              const ItemIcon = resolvePresetIcon(preset.icon);
              return (
                <DropdownMenuItem
                  key={preset.handle}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePresetClick(preset);
                  }}
                  className={cn(
                    "flex items-center justify-between",
                    activePreset === preset.handle && "bg-accent"
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {ItemIcon && (
                      <ItemIcon
                        size="0.875rem"
                        className="text-muted-foreground shrink-0"
                      />
                    )}
                    <span className="truncate">
                      {preset.title || preset.handle}
                    </span>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
        </div>
        {onCustomClick && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleCustomClick();
              }}
              className={cn(
                "text-primary",
                activePreset === "Custom" && "bg-accent"
              )}
            >
              <Settings2 size="0.875rem" className="shrink-0" />
              Custom models
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const PRESET_PREFIX = "preset:";

/** Check if a form value is a preset reference like ["preset:chat-cost"]. */
function isPresetRef(value: string[]): boolean {
  return value.length === 1 && value[0].startsWith(PRESET_PREFIX);
}

/** Extract the handle from a preset ref (e.g. "preset:chat-cost" → "chat-cost"). */
function parsePresetHandle(value: string[]): string {
  return value[0].slice(PRESET_PREFIX.length);
}

/** Expand a single form-value entry into the rows it represents.
 *
 *  - **Qualified slug** (``"openai:gpt-4o"``) matches at most one row by
 *    ``row.id`` (the qualified slug). Returns ``[that row]``.
 *  - **Bare slug** (``"gpt-4o"`` from a legacy config or preset) returns
 *    EVERY per-link row for that model so the picker shows the full
 *    fallback chain explicitly. The form write-back then stores the
 *    corresponding qualified slugs, preserving the expansion verbatim
 *    on the next save instead of silently pinning to one provider.
 *  - Models with no providers linked yet still match by name and return
 *    the single bare row from the backend. */
function expandStoredValueToRows(
  rows: IModelExtended[],
  stored: string
): IModelExtended[] {
  const exactById = rows.find((m) => m.id === stored);
  if (exactById) return [exactById];
  return rows.filter((m) => m.name === stored);
}

const InnerModelSelectField = ({
  name,
  endpoint,
  modelType,
  defaultValue,
  required,
  visibleModelsUpdatedData,
  allModelsUpdatedData,
  label,
  maxModels,
  isDisabled,
  showPresets,
  showInlinePresets = true,
  layout = "stacked",
  hideDisabledModels = true,
  defaultPreset,
}: IInnerModelSelectField) => {
  const form = useFormContext();
  const { nodeId, fieldKey } = useContext(PinFieldContext);
  const { isEditorV2 } = useContext(ConfigFormRendererContext);
  const showPinHandle = isEditorV2 && nodeId && fieldKey;

  // Ensure the field exists on the form so subsequent setValue() calls work.
  // Without this, setValue() on a field absent from defaultValues is ignored.
  // A layout effect, not render: setting it while rendering re-renders every
  // form subscriber (the node config panel) in the middle of this render.
  useLayoutEffect(() => {
    if (form.getValues(name) === undefined) {
      form.setValue(name, []);
    }
  }, [form, name]);

  const [isOpen, setIsOpen] = useState(false);
  const [presetResetKey, setPresetResetKey] = useState(0);

  const getInitialValue = () => {
    const formValue =
      (form.getValues(name) as string | string[] | undefined) ?? [];

    if (formValue) {
      const result = Array.isArray(formValue) ? formValue : [formValue];
      return result;
    } else {
      if (defaultValue) {
        const result = Array.isArray(defaultValue)
          ? defaultValue
          : [defaultValue];
        return result;
      }
      return [] as string[];
    }
  };

  const initialValue = useRef(getInitialValue()).current;
  const initialIsPreset = isPresetRef(initialValue);

  // Track whether the form value is a preset handle or an explicit model list.
  // When non-null and not "Custom", form value is ["preset:<handle>"].
  const [activePreset, setActivePreset] = useState<string | null>(
    initialIsPreset ? parsePresetHandle(initialValue) : null
  );

  // Track whether a preset handle is being used as the form value
  // (as opposed to an expanded model list).
  const [isPresetMode, setIsPresetMode] = useState(initialIsPreset);

  const { spotGroup } = useSpotUser();
  const { data: presetsResponse } = useQuery({
    queryKey: [DATA_KEYS.MODEL_PRESETS, spotGroup.id],
    queryFn: () =>
      PresetsService.listPresets({
        path: { group_id: spotGroup.id },
      }),
    enabled: showPresets || showInlinePresets,
  });

  // Provider logos for the chip rendered on each Chosen-models row. The
  // catalog page hits the same endpoint so the cache is shared.
  const { data: providerDefinitionsResponse } = useQuery({
    queryKey: [DATA_KEYS.PROVIDER_DEFINITIONS, "model", spotGroup.id],
    queryFn: () =>
      ProvidersService.listProviderDefinitions({
        path: { group_id: spotGroup.id },
        query: { category: "model" },
      }),
    enabled: !!spotGroup.id,
    staleTime: 5 * 60_000,
  });
  // Logos keyed on the no-llm provider TYPE rather than the display
  // name — multi-instance tenants rename their rows ("Corp OpenAI"),
  // and a name-keyed lookup would silently miss the logo.
  const modelTitleByName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of allModelsUpdatedData) map[m.name] = m.title;
    return map;
  }, [allModelsUpdatedData]);

  const providerLogoByType = useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of (providerDefinitionsResponse?.data ?? []) as Array<{
      provider_name: string;
      provider_logo?: string | null;
      provider_config?: Record<string, unknown>;
    }>) {
      const typeField = d.provider_config?.type as
        | { default?: unknown }
        | undefined;
      const t =
        typeof typeField?.default === "string" ? typeField.default : null;
      if (t && d.provider_logo) map[t] = d.provider_logo;
    }
    return map;
  }, [providerDefinitionsResponse]);

  const presetsArray = useMemo(() => {
    const presets = presetsResponse?.data;
    if (!presets) return [];
    return (presets as PresetResponse[]).filter((p) => p.is_active !== false);
  }, [presetsResponse]);

  // Resolve initial preset handle on mount
  const initialPresetResolved = useRef(false);

  // Needed for the NoxusSortable component to work
  const [selectedModels, setSelectedModels] = useState<IModelExtended[]>(() => {
    // If the initial value is a preset ref, we can't resolve yet (presets may not be loaded).
    // Models will be resolved in the useEffect below once presets are available.
    if (isPresetRef(initialValue)) {
      return [];
    }
    if (allModelsUpdatedData && allModelsUpdatedData.length > 0) {
      // ``expandStoredValueToRows`` returns ALL per-link rows for a bare
      // slug — flatMap unfolds them so the picker shows the full fallback
      // chain. The write-back effect then stamps the stored form value
      // with the corresponding qualified slugs.
      return initialValue.flatMap((stored: string) =>
        expandStoredValueToRows(allModelsUpdatedData, stored)
      );
    }
    return [];
  });

  // Resolve preset handle from initial form value once presets are loaded
  useEffect(() => {
    if (initialPresetResolved.current) return;

    if (isPresetRef(initialValue)) {
      const handle = parsePresetHandle(initialValue);
      setActivePreset(handle);
      setIsPresetMode(true);

      // Resolve models for display from presets data
      if (presetsArray.length > 0 && allModelsUpdatedData.length > 0) {
        const preset = presetsArray.find((p) => p.handle === handle);
        if (preset) {
          const resolved = preset.models.flatMap((modelName) =>
            expandStoredValueToRows(allModelsUpdatedData, modelName)
          );
          setSelectedModels(resolved);
        } else {
          // Preset handle not found — reset to empty dropdown state.
          // Backend check_models() will surface the validation error.
          setActivePreset(null);
          setIsPresetMode(false);
        }
        initialPresetResolved.current = true;
      } else if (presetsArray.length > 0) {
        // Presets loaded but no models data yet — check if preset exists
        const preset = presetsArray.find((p) => p.handle === handle);
        if (!preset) {
          setActivePreset(null);
          setIsPresetMode(false);
          initialPresetResolved.current = true;
        }
      }
    } else if (initialValue.length === 0 && defaultPreset) {
      // No form value yet but a default preset is specified — apply it
      if (presetsArray.length > 0 && allModelsUpdatedData.length > 0) {
        const preset = presetsArray.find((p) => p.handle === defaultPreset);
        if (preset) {
          const resolved = preset.models.flatMap((modelName) =>
            expandStoredValueToRows(allModelsUpdatedData, modelName)
          );
          setSelectedModels(resolved);
          setActivePreset(preset.handle);
          setIsPresetMode(true);
          form.setValue(name, [`preset:${preset.handle}`], {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
        initialPresetResolved.current = true;
      }
    } else {
      // Explicit model list — try to match against a preset, otherwise "Custom"
      if (showPresets || showInlinePresets) {
        if (presetsArray.length > 0) {
          const matchingPreset = presetsArray.find((p) => {
            if (p.models.length !== initialValue.length) return false;
            return p.models.every((m, i) => m === initialValue[i]);
          });
          setActivePreset(matchingPreset?.handle ?? "Custom");
        } else {
          setActivePreset("Custom");
        }
      }
      // Re-expand when allModelsUpdatedData arrives. The useState
      // initializer ran before the query resolved so selectedModels
      // started as []. Don't mark resolved until we've actually
      // hydrated the model rows.
      if (allModelsUpdatedData.length > 0 && initialValue.length > 0) {
        const resolved = initialValue.flatMap((stored: string) =>
          expandStoredValueToRows(allModelsUpdatedData, stored)
        );
        if (resolved.length > 0) setSelectedModels(resolved);
        initialPresetResolved.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetsArray, allModelsUpdatedData]);

  // Keep displayed models in sync when preset data refreshes (e.g. after editing a preset)
  useEffect(() => {
    if (!isPresetMode || !activePreset || activePreset === "Custom") return;
    if (presetsArray.length === 0 || allModelsUpdatedData.length === 0) return;

    const preset = presetsArray.find((p) => p.handle === activePreset);
    if (!preset) return;

    const resolved = preset.models.flatMap((modelName) =>
      expandStoredValueToRows(allModelsUpdatedData, modelName)
    );
    setSelectedModels(resolved);
  }, [presetsArray, allModelsUpdatedData, isPresetMode, activePreset]);

  //filter undefined items
  // Merge selected models with latest status from the data query, then
  // filter to displayable ones. Single memo replaces the previous
  // 3-layer chain (filteredSelectedModels → updatedFilteredSelectedModels
  // → displaySelectedModels).
  const updatedFilteredSelectedModels = useMemo(() => {
    return selectedModels
      .filter((model) => model !== undefined)
      .map((selectedModel) => {
        const updatedModel =
          allModelsUpdatedData.find((m) => m.id === selectedModel.id) ??
          allModelsUpdatedData.find((m) => m.name === selectedModel.name);

        if (updatedModel) {
          return {
            ...selectedModel,
            is_valid: updatedModel.is_valid,
            is_available: updatedModel.is_available,
            is_active: updatedModel.is_active,
            provider_is_active: updatedModel.provider_is_active,
            has_link: updatedModel.has_link,
          };
        }
        return selectedModel;
      });
  }, [selectedModels, allModelsUpdatedData]);

  const displaySelectedModels = useMemo(() => {
    const withLink = updatedFilteredSelectedModels.filter(
      (m) => "has_link" in m && m.has_link !== false
    );
    if (!hideDisabledModels) return withLink;
    return withLink.filter((m) => m.is_active !== false);
  }, [updatedFilteredSelectedModels, hideDisabledModels]);

  // Tracks whether the user has actually changed the selection. Without this,
  // mount-time bare→qualified expansion of the stored value would flip
  // ``isDirty`` on a freshly-loaded form (the user sees "unsaved changes" on a
  // KB they never touched). Stored values are only rewritten on real user
  // interaction.
  const userChangedRef = useRef(false);

  /* Responsible for updating the form */
  useEffect(() => {
    if (isPresetMode) return;
    if (!userChangedRef.current) return;

    if (allModelsUpdatedData && allModelsUpdatedData.length > 0) {
      form.setValue(
        name,
        updatedFilteredSelectedModels.map((model) => model.slug ?? model.name),
        { shouldDirty: true, shouldValidate: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedFilteredSelectedModels, isPresetMode]);

  // Called when a preset is selected from PresetSelector
  const handlePresetApply = useCallback(
    (handle: string, models: IModelExtended[]) => {
      setSelectedModels(models);
      setActivePreset(handle);
      setIsPresetMode(true);
      form.setValue(name, [`preset:${handle}`], {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form, name]
  );

  // Called when models are manually changed (reorder, modal pick)
  const selectedModelsRef = useRef(selectedModels);
  selectedModelsRef.current = selectedModels;

  const handleCustomModelsChange: Dispatch<SetStateAction<IModelExtended[]>> =
    useCallback((action) => {
      const models =
        typeof action === "function"
          ? action(selectedModelsRef.current)
          : action;
      userChangedRef.current = true;
      setSelectedModels(models);
      setActivePreset("Custom");
      setIsPresetMode(false);
      setPresetResetKey((k) => k + 1);
    }, []);

  return (
    <ErrorBoundary
      fallback={
        <NoxusAlert
          look="banner"
          title="There was an issue loading the model selection. Please refresh the page and try again."
          variant="destructive"
        />
      }
    >
      <div>
        <div className="w-full border border-border rounded-xl p-4">
          <div className="w-full flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <p className="text-sm font-medium">Chosen models</p>
              {showPinHandle && (
                <PinHandle nodeId={nodeId} fieldKey={fieldKey} />
              )}
            </div>
            <div className="flex items-center gap-2">
              {showInlinePresets && (
                <PresetSelector
                  key={presetResetKey}
                  allModelsUpdatedData={allModelsUpdatedData}
                  onSelectPreset={(models) => handleCustomModelsChange(models)}
                  onPresetApply={handlePresetApply}
                  isDisabled={isDisabled}
                  onCustomClick={() => setIsOpen(true)}
                  activePreset={activePreset}
                  onActivePresetChange={setActivePreset}
                />
              )}
            </div>
          </div>
          <p className="text-muted-foreground text-xs mb-4">
            Your first selected model will be set as the default. If it
            encounters an error, the next one in line will be used
            automatically.
          </p>

          {displaySelectedModels.some(
            (model) =>
              model.is_active === false || model.provider_is_active === false
          ) && (
            <NoxusAlert
              className="mb-4"
              variant="warning"
              size="xs"
              title={`${modelNounTitle(modelType)} not available`}
              description={`Some selected ${modelNoun(modelType)} are disabled or not configured. You can enable them on the Models page in Settings.`}
              action={{
                label: (
                  <>
                    <ExternalLink size={12} />
                    Go to Models
                  </>
                ),
                onClick: () =>
                  window.open(
                    "/settings/platform/models",
                    "_blank",
                    "noopener"
                  ),
              }}
            />
          )}

          <ProviderLogoContext.Provider value={providerLogoByType}>
            <ModelTitleContext.Provider value={modelTitleByName}>
              {displaySelectedModels.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <NoxusSortable
                    items={displaySelectedModels}
                    setItems={handleCustomModelsChange}
                    SortableItem={SortableModelItem}
                    isDisabled={isDisabled}
                    className="max-h-40 overflow-y-auto"
                  />
                  {(activePreset === "Custom" || !showInlinePresets) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsOpen(true)}
                      isDisabled={isDisabled}
                      className="gap-1.5 w-full mt-4"
                    >
                      <Settings2 size="0.75rem" />
                      Edit models
                    </Button>
                  )}
                </div>
              ) : !showInlinePresets || activePreset === "Custom" ? (
                <>
                  <NoxusAlert
                    look="banner"
                    size="xs"
                    title={
                      activePreset === "Custom" && showInlinePresets
                        ? "No models selected. Choose a preset or edit the Custom models."
                        : "No models selected. Click below to pick models."
                    }
                    variant="warning"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 w-full mt-4"
                    onClick={() => setIsOpen(true)}
                    isDisabled={isDisabled}
                  >
                    {activePreset === "Custom" && showInlinePresets ? (
                      <Settings2 size="1rem" strokeWidth="1.25px" />
                    ) : (
                      <MousePointer2 size="1rem" strokeWidth="1.25px" />
                    )}
                    {activePreset === "Custom" && showInlinePresets
                      ? "Edit models"
                      : "Pick models"}
                  </Button>
                </>
              ) : (
                <NoxusAlert
                  variant="warning"
                  size="xs"
                  title={`Preset ${modelNoun(modelType)} not configured`}
                  description={`You can add ${modelNoun(modelType)} in the Models page in Settings.`}
                />
              )}

              <ModelModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                models={visibleModelsUpdatedData}
                modelType={modelType}
                selectedModels={displaySelectedModels}
                setSelectedModels={handleCustomModelsChange}
                onPresetApply={handlePresetApply}
                customValue={form.getValues(name)}
                minModels={required ? 1 : 0}
                maxModels={maxModels}
                layout={layout}
                allModelsData={allModelsUpdatedData}
                showPresets={showPresets}
                hideDisabledModels={hideDisabledModels}
                providerLogoByType={providerLogoByType}
              />
            </ModelTitleContext.Provider>
          </ProviderLogoContext.Provider>
        </div>
      </div>
    </ErrorBoundary>
  );
};
